import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCliente } from '@/hooks/useClientes';
import { useAuthContext } from '@/contexts/AuthContext';
import { ArrowLeft, Phone, User, Loader2, Mail, MapPin } from 'lucide-react';

/**
 * Página de visualização de cliente para PRESTADORES.
 * Exibe apenas dados básicos (nome, telefone, e-mail, endereço).
 * NÃO exibe mensagens, histórico, tags, observações ou ações administrativas.
 */
export default function ClienteParaPrestador() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isPrestador } = useAuthContext();
  
  const { data: cliente, isLoading } = useCliente(id);

  // Segurança: apenas prestadores podem acessar esta página
  // Admins e atendentes devem usar /cliente-potential/:id
  if (!isPrestador) {
    navigate(`/cliente-potential/${id}`, { replace: true });
    return null;
  }

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!cliente) {
    return (
      <MainLayout>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">Cliente não encontrado</p>
          <Button variant="link" onClick={() => navigate(-1)}>Voltar</Button>
        </div>
      </MainLayout>
    );
  }

  // Formatar endereço para exibição
  const formatarEndereco = () => {
    const partes = [];
    if (cliente.endereco_rua) {
      let rua = cliente.endereco_rua;
      if (cliente.endereco_numero) rua += `, ${cliente.endereco_numero}`;
      partes.push(rua);
    }
    if (cliente.endereco_complemento) partes.push(cliente.endereco_complemento);
    if (cliente.endereco_codigo_postal || cliente.endereco_cidade) {
      partes.push(`${cliente.endereco_codigo_postal || ''} ${cliente.endereco_cidade || ''}`.trim());
    }
    if (cliente.endereco_pais) partes.push(cliente.endereco_pais);
    return partes.length > 0 ? partes.join('\n') : null;
  };

  const enderecoFormatado = formatarEndereco();

  return (
    <MainLayout>
      <div className="p-6 max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Dados do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Nome */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Nome</p>
              <p className="text-lg font-medium">{cliente.nome || 'Não informado'}</p>
            </div>

            {/* Telefone */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Telefone</p>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <a 
                  href={`tel:${cliente.telefone}`} 
                  className="text-lg font-medium text-primary hover:underline"
                >
                  {cliente.telefone}
                </a>
              </div>
            </div>

            {/* E-mail */}
            {cliente.email && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">E-mail</p>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={`mailto:${cliente.email}`} 
                    className="text-lg font-medium text-primary hover:underline"
                  >
                    {cliente.email}
                  </a>
                </div>
              </div>
            )}

            {/* Endereço */}
            {enderecoFormatado && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Endereço</p>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                  <p className="whitespace-pre-line">{enderecoFormatado}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-sm text-muted-foreground text-center mt-6">
          Para mais informações, entre em contato com o atendente responsável.
        </p>
      </div>
    </MainLayout>
  );
}
